import { isSensitiveKey, redactSensitive, redactText } from './redact-sensitive.util';

/** Longest request or response body kept in the network log, in characters. */
export const LOG_BODY_MAX_LENGTH = 5000;

/** Longest console message kept in the console log, in characters. */
export const LOG_CONSOLE_MAX_LENGTH = 2000;

const TRUNCATED_SUFFIX = '…[truncated]';

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}${TRUNCATED_SUFFIX}` : text;
}

/**
 * Keeps the headers that are safe to share, dropping every header whose name is sensitive
 * (`authorization`, `cookie`, `set-cookie`, API keys, tokens, sessions...).
 *
 * @param headers - Header names and values as captured.
 * @example
 * redactHeaders({ Authorization: 'Bearer abc', 'content-type': 'application/json' });
 * // { 'content-type': 'application/json' }
 */
export function redactHeaders(headers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(headers).filter(([name]) => !isSensitiveKey(name)));
}

/**
 * Turns a request or response body into text for the network log: JSON is redacted field by
 * field (any depth), anything else goes through the free-text redactor, uploads become a
 * placeholder and the result is cut to `LOG_BODY_MAX_LENGTH`.
 *
 * @param body - Body as the HTTP layer saw it (string, object, FormData, Blob...).
 * @returns The text to store, or `null` when there was no body.
 * @example
 * redactLogBody('{"access_token":"abc","name":"x"}'); // '{"access_token":"[REDACTED]","name":"x"}'
 */
export function redactLogBody(body: unknown): string | null {
  if (body === null || body === undefined) return null;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return '[FormData]';
  if (typeof Blob !== 'undefined' && body instanceof Blob) return '[Blob]';
  if (typeof body === 'string') {
    try {
      return truncate(JSON.stringify(redactSensitive(JSON.parse(body))), LOG_BODY_MAX_LENGTH);
    } catch {
      return truncate(redactText(body, LOG_BODY_MAX_LENGTH), LOG_BODY_MAX_LENGTH);
    }
  }
  try {
    return truncate(JSON.stringify(redactSensitive(body)), LOG_BODY_MAX_LENGTH);
  } catch {
    return '[Unserializable]';
  }
}

/**
 * Turns the arguments of a `console.*` call into one redacted line: tokens, JWTs and sensitive
 * fields are replaced, and the line is cut to `LOG_CONSOLE_MAX_LENGTH`.
 *
 * @param args - Arguments exactly as passed to the console method.
 * @example
 * redactConsoleArgs(['Bearer eyJhbGciOi.abc.def', { password: 'x', name: 'y' }]);
 * // 'Bearer [REDACTED] {"password":"[REDACTED]","name":"y"}'
 */
export function redactConsoleArgs(args: readonly unknown[]): string {
  const parts = args.map((arg) => {
    if (typeof arg === 'string') return redactText(arg, LOG_CONSOLE_MAX_LENGTH);
    if (arg instanceof Error) return redactText(`${arg.name}: ${arg.message}`, LOG_CONSOLE_MAX_LENGTH);
    try {
      return JSON.stringify(redactSensitive(arg)) ?? String(arg);
    } catch {
      return '[Unserializable]';
    }
  });
  return truncate(parts.join(' '), LOG_CONSOLE_MAX_LENGTH);
}
